import React, { useState } from 'react';
import { useField } from 'react-final-form';
import { Badge } from '@tcl-boron-prefabs/badge';
import { Information } from '@tcl-boron-icons/icons';
import { Tooltip } from '@tcl-argon-prefabs/tooltip';
import { RELATIONSHIP_TO_PATIENT_NAME_V4, RelationshipToPatientV4 } from '@tempus/patient-forms-service-shared';

import { isOktaAuthenticated } from '../../../../api/Okta';

import styles from '../../shared/styles.module.css';

export const AUTHENTICATED_LABEL = 'User authenticated';
export const UNAUTHENTICATED_LABEL = 'User not authenticated';
export const UNAUTHENTICATED_TOOLTIP = 'You will be required to authenticate on form submission.';
export const UNAUTHENTICATED_ICON_LABEL = 'Authentication information';

// Badge variants come from the TCL palettes: 6 is SuccessPalette, 8 is WarningPalette.
const AUTHENTICATED_VARIANT = 6;
const UNAUTHENTICATED_VARIANT = 8;

/**
 * Owns its own open state so that it is discarded whenever the badge stops rendering. Hovering the
 * icon and then changing the relationship removes the icon without a matching mouse leave, which
 * would otherwise leave the tooltip open the next time a Tempus representative is selected.
 */
const AuthenticationHelpTooltip: React.FunctionComponent = () => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Tooltip isOpen={isOpen} position="below-left">
      {{
        base: (
          <Information
            aria-label={UNAUTHENTICATED_ICON_LABEL}
            className={styles.authStatusBadgeIcon}
            tabIndex={0}
            onMouseEnter={() => setIsOpen(true)}
            onMouseLeave={() => setIsOpen(false)}
            onFocus={() => setIsOpen(true)}
            onBlur={() => setIsOpen(false)}
          />
        ),
        tooltip: UNAUTHENTICATED_TOOLTIP,
      }}
    </Tooltip>
  );
};

/**
 * Shows whether a Tempus representative has already authenticated with Okta. Submission asks the
 * same question of the relationship and reads the same Okta session, so the badge cannot promise
 * an authentication step the submit path will not take. Representatives authenticate on
 * submission, so the session is only read on render and does not need to update.
 */
const TempusRepresentativeAuthBadge: React.FunctionComponent = () => {
  const { input: relationshipToPatient } = useField<RelationshipToPatientV4>(RELATIONSHIP_TO_PATIENT_NAME_V4, {
    subscription: { value: true },
  });

  if (relationshipToPatient.value !== RelationshipToPatientV4.TEMPUS_REPRESENTATIVE) {
    return null;
  }

  const isAuthenticated = isOktaAuthenticated();

  return (
    <div className={styles.authStatusBadge}>
      <Badge
        small
        badgeType="secondary"
        badgeVariant={isAuthenticated ? AUTHENTICATED_VARIANT : UNAUTHENTICATED_VARIANT}
        text={isAuthenticated ? AUTHENTICATED_LABEL : UNAUTHENTICATED_LABEL}
        className={styles.authStatusBadgeLabel}
      />
      {!isAuthenticated && <AuthenticationHelpTooltip />}
    </div>
  );
};

export default TempusRepresentativeAuthBadge;
